import React from 'react'
import { Button, InputTextAreaHighlightable, Select } from 'components/atoms'
import styled from 'styled-components'
import { IBuildedTour, IWeeklyTourMemoTemplate } from 'utils/weeklyTypes'
import * as constants from 'static/constants'
import * as interfaces from 'static/interfaces'
import { agentService } from 'core/services'
import { replaceVariableForMessage } from 'utils/soundoutOrFormalRequest'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

interface IProps {
  tour: IBuildedTour
  tourMemoTemplates: IWeeklyTourMemoTemplate[]
  onTourUpdate: (tour: any) => void
}

interface IInputSectionProps {
  label: string
  children: React.ReactNode
  className?: string
}

const InputSection: React.FC<IInputSectionProps> = ({ label, children, className = '' }) => (
  <div className={`input-section ${className}`}>
    <div className="label">{label}</div>
    <div className="input-container">{children}</div>
  </div>
)

const getTemplateOptions = (templates: IWeeklyTourMemoTemplate[], tour: IBuildedTour) => {
  const targetPostIds = [tour.post_id, tour.post.family_owner_post_id]
  return templates
    .filter(template => template.posts.some(post => targetPostIds.includes(post.id)))
    .map(template => ({
      label: template.title,
      value: template.id.toString(),
    }))
}

const getMessageByTemplateId = (
  id: string,
  templates: IWeeklyTourMemoTemplate[],
  tour: IBuildedTour
) => {
  const template = templates.find(t => t.id.toString() === id)
  return template ? replaceVariableForMessage(template.message, tour) : ''
}

export const TourMemoModalBody: React.FC<IProps> = ({ tour, tourMemoTemplates, onTourUpdate }) => {
  const templateOptions = React.useMemo(() => getTemplateOptions(tourMemoTemplates, tour), [
    tourMemoTemplates,
    tour,
  ])
  const selectOptions = React.useMemo(
    () => [{ label: '', value: '', disabled: true, hidden: true }, ...templateOptions],
    [templateOptions]
  )

  const [selectedTemplateId, setSelectedTemplateId] = React.useState('')
  const [message, setMessage] = React.useState<string>(tour.memo ?? '')
  const [saving, setSaving] = React.useState(false)

  const handleTemplateChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const templateId = e.target.value
      setSelectedTemplateId(templateId)
      setMessage(getMessageByTemplateId(templateId, tourMemoTemplates, tour))
    },
    [tourMemoTemplates, tour]
  )

  const handleMessageChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value)
  }, [])

  const handleSave = React.useCallback(async () => {
    if (tour.memo_type !== 'custom') {
      if (
        !confirm(
          'このツアーメモを個別に保存します。以後、デフォルトテンプレートの内容は自動反映されません。よろしいですか?'
        )
      ) {
        return
      }
    }
    if (saving) {
      return
    }

    setSaving(true)
    try {
      const { tour: updatedTour } = await agentService.updateTourMemo(tour.id, { memo: message })
      onTourUpdate(updatedTour)
      window.globalModal.closeModal()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }, [saving, tour.id, message, onTourUpdate])

  return (
    <ModalBody>
      <div className="header">
        <div>ツアーメモ</div>
        <SvgIcon
          className="close-button"
          name="close"
          width={24}
          height={24}
          role="button"
          onClick={() => window.globalModal.closeModal()}
        />
      </div>
      <div className="contents">
        <InputSection label="テンプレート">
          <div className="select">
            <Select
              name="tourMemoTemplate"
              options={selectOptions}
              value={selectedTemplateId}
              onChangeHandler={handleTemplateChange}
            />
          </div>
          <a
            href={`/agent/posts/${tour.post.id}/edit?active=settings`}
            target="_blank"
            rel="noopener noreferrer"
          >
            ツアー設定
          </a>
        </InputSection>
        <InputSection label="メモ" className="top">
          <div className="memo-wrapper">
            <p className="memo-note">※ガイドにも公開されるメモです</p>
            <p className="memo-note link-note">
              リンクを埋め込む場合は<code>[表示テキスト](https://...)</code>
              のマークダウン形式で入力してください
            </p>
            <InputTextAreaHighlightable
              name="memo"
              placeholder="メモを入力してください"
              className="message-textarea"
              value={message}
              onChangeHandler={handleMessageChange}
            />
            {tour.memo_type === 'custom' && (
              <p style={{ fontSize: '12px', color: constants.COLORS.info3, fontWeight: 600 }}>
                ※個別保存済み(テンプレート自動反映なし)
              </p>
            )}
          </div>
        </InputSection>
        <div className="footer">
          <Button
            borderColor={constants.COLORS.primary}
            handleClick={() => window.globalModal.closeModal()}
          >
            キャンセル
          </Button>
          <Button primary={true} disabled={saving} handleClick={handleSave}>
            決定
          </Button>
        </div>
      </div>
    </ModalBody>
  )
}

const ModalBody = styled.div`
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
    font-size: 18px;
    border-bottom: 1px solid ${constants.COLORS.divider};
    padding: 18px 24px;

    .close-button {
      cursor: pointer;
      width: 24px;
      height: 24px;
    }
  }

  .contents {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;

    .input-section {
      display: flex;
      align-items: center;
      gap: 24px;
      &.top {
        align-items: flex-start;
      }
      .label {
        font-weight: 600;
        text-align: right;
        min-width: 222px;
        flex: 0;
      }

      .input-container {
        .select {
          max-width: 200px;
          width: 200px;
        }
        > div {
          width: 100%;
        }
        > a {
          color: ${constants.COLORS.primary};
          text-decoration: underline;
        }

        font-size: 14px;
        display: flex;
        align-items: center;
        width: 100%;
        gap: 16px;

        .memo-wrapper {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .memo-note {
          font-size: 16px;
        }

        .link-note {
          font-size: 12px;
          color: ${constants.COLORS.subText};
        }

        .message-textarea {
          height: 320px;
        }
      }
    }
    .footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
`
